
import React, { useState } from 'react';
import { Bitcoin, CreditCard } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

interface BitcoinVerificationFormProps {
  onBitcoinVerified: (address: string, amount: number) => void;
  onBack: () => void;
  isVerifying: boolean;
}

const BitcoinVerificationForm: React.FC<BitcoinVerificationFormProps> = ({
  onBitcoinVerified,
  onBack,
  isVerifying
}) => {
  const [bitcoinAddress, setBitcoinAddress] = useState('');
  const [isAddressValid, setIsAddressValid] = useState(false);
  
  // Basic Bitcoin address validation
  const validateBitcoinAddress = (address: string): boolean => {
    // Simple validation for Bitcoin addresses (starts with 1, 3, or bc1)
    const regex = /^(1|3|bc1)[a-zA-Z0-9]{25,62}$/;
    return regex.test(address);
  };

  const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const address = e.target.value;
    setBitcoinAddress(address);
    setIsAddressValid(validateBitcoinAddress(address));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isAddressValid) {
      toast.error('Please enter a valid Bitcoin address');
      return;
    }
    
    // Verify with a small test amount of BTC
    const testAmountBTC = 0.0001; // Very small amount for verification
    onBitcoinVerified(bitcoinAddress, testAmountBTC);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
        <h3 className="text-lg font-medium text-amber-800 mb-2">Bitcoin Verification</h3>
        <p className="text-sm text-amber-600">
          Verify your identity with a small Bitcoin transaction. This helps secure your account and enables Bitcoin payments.
        </p>
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="bitcoin-address">Bitcoin Address</Label>
        <Input
          id="bitcoin-address"
          placeholder="Enter your Bitcoin address (starts with 1, 3, or bc1)"
          value={bitcoinAddress}
          onChange={handleAddressChange}
          className={isAddressValid ? 'border-green-500' : ''}
        />
        {bitcoinAddress && !isAddressValid && (
          <p className="text-sm text-red-500">Please enter a valid Bitcoin address</p>
        )}
      </div>
      
      <div className="flex justify-between mt-6">
        <Button type="button" variant="outline" onClick={onBack}>
          Back
        </Button>
        <Button 
          type="submit" 
          disabled={!isAddressValid || isVerifying}
          className="bg-amber-500 hover:bg-amber-600"
        >
          {isVerifying ? 'Verifying...' : 'Verify with Bitcoin'}
        </Button>
      </div>
    </form>
  );
};

export default BitcoinVerificationForm;
